import React, { useState, useEffect } from 'react';
import { RolePercentage, getRolePercentages, saveRolePercentages, deleteRolePercentage } from '../services/roleService';
import { TenantSettings, getSettings, saveSettings } from '../services/settingsService';
import { ROLES, ELIGIBLE_ALLOCATION_ROLES, isManagerialRole } from '../constants';
import { Shield, Percent, Plus, Trash2, AlertCircle, CheckCircle2, ChevronRight, ShieldAlert, Scale, Info, ShieldCheck } from 'lucide-react';

interface Props {
  tenantId: string;
}

export const RolePercentageConfig: React.FC<Props> = ({ tenantId }) => {
  const [roles, setRoles] = useState<RolePercentage[]>([]);
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [status, setStatus] = useState('');
  const [newRole, setNewRole] = useState(ELIGIBLE_ALLOCATION_ROLES[0] || 'Stylist');
  const [customRoleInput, setCustomRoleInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [roleNotice, setRoleNotice] = useState<{ role: string; action: 'add' | 'percentage' | 'eligibility' } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const [roleData, settingsData] = await Promise.all([
        getRolePercentages(tenantId),
        getSettings(tenantId)
      ]);
      setRoles(roleData);
      setSettings(settingsData);
    };
    fetchData();
  }, [tenantId]);

  const handleAllocationMethodChange = (method: string) => {
    setSettings(prev => prev ? { ...prev, allocationMethod: method } : null);
    
    // If switching away from role-based, clear percentages
    if (!['Split by Role', 'Split by Role by Shift'].includes(method)) {
      setRoles(prev => prev.map(role => ({ ...role, percentage: 0 })));
    }
  };

  const handlePercentageChange = (id: string, value: string) => {
    const target = roles.find(r => r.id === id);
    const numVal = value === '' ? 0 : parseFloat(value) || 0;
    if (target && isManagerialRole(target.role) && numVal > 0) {
      setRoleNotice({ role: target.role, action: 'percentage' });
      return;
    }
    const newRoles = roles.map(role =>
      role.id === id ? { ...role, percentage: numVal } : role
    );
    setRoles(newRoles);
  };

  const handleEligibilityChange = (id: string, value: boolean) => {
    const target = roles.find(r => r.id === id);
    if (target && isManagerialRole(target.role) && value === true) {
      setRoleNotice({ role: target.role, action: 'eligibility' });
      return;
    }
    const newRoles = roles.map(role =>
      role.id === id ? { ...role, isEligible: value } : role
    );
    setRoles(newRoles);
  };

  const handleAddRole = () => {
    const roleToAdd = showCustomInput ? customRoleInput.trim() : newRole;
    if (!roleToAdd) return;

    if (isManagerialRole(roleToAdd)) {
      setRoleNotice({ role: roleToAdd, action: 'add' });
      return;
    }

    if (!roles.find(r => r.role.toLowerCase() === roleToAdd.toLowerCase())) {
      const sanitizedId = roleToAdd.replace(/\//g, '_').toLowerCase();
      setRoles(prev => [
        ...prev, 
        { 
          id: sanitizedId, 
          role: roleToAdd, 
          percentage: 0, 
          isEligible: true, 
          tenantId 
        }
      ]);
      if (showCustomInput) {
        setCustomRoleInput('');
        setShowCustomInput(false);
      }
    }
  };

  const handleRemoveRole = async (roleId: string, roleName: string) => {
    const updated = roles.filter(r => r.id !== roleId);
    setRoles(updated);
    try {
      await deleteRolePercentage(tenantId, roleId);
      setStatus(`Removed "${roleName}" from allocations`);
      setTimeout(() => setStatus(''), 3000);
    } catch (error) {
      console.error('Error removing role from database:', error);
    }
  };

  const total = roles.filter(r => r.isEligible).reduce((sum, role) => sum + role.percentage, 0);
  
  const isRoleBasedMethod = ['Split by Role', 'Split by Role by Shift'].includes(settings?.allocationMethod || '');
  const isSaveDisabled = !settings?.allocationMethod || (isRoleBasedMethod && total !== 100);

  const handleSave = async () => {
    if (isSaveDisabled) return;
    try {
      if (!settings) return;
      await Promise.all([
        saveRolePercentages(tenantId, roles),
        saveSettings(tenantId, settings)
      ]);
      setStatus('Configuration saved!');
      setTimeout(() => setStatus(''), 3000);
    } catch (error) {
      console.error('Error saving configuration:', error);
      setStatus('Error saving configuration.');
    }
  };

  return (
    <div className="space-y-6 md:space-y-8 max-w-5xl mx-auto">
      {/* Header & Main Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-gray-100 pb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight uppercase font-sans flex items-center gap-2">
            <Scale size={22} className="text-blue-600" />
            Tip Allocations & Role Rules
          </h2>
          <p className="text-gray-500 mt-1 font-medium text-xs md:text-sm">
            Configure global tip allocation methods, manage participating roles, and set split percentages.
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-4 w-full sm:w-auto">
          {/* Allocation Method Selector */}
          <div className="w-full sm:w-56">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1 block mb-1">
              Allocation Method <span className="text-red-500">*</span>
            </label>
            <select 
              value={settings?.allocationMethod || ''} 
              onChange={e => handleAllocationMethodChange(e.target.value)} 
              className="input-field w-full text-xs font-bold"
            >
              <option value="">-- Select method --</option>
              <option value="Equal split">Equal split</option>
              <option value="Equal % by shift only">Equal % by shift only</option>
              <option value="Split by Role">Split by Role</option>
              <option value="Split by Role by Shift">Split by Role by Shift</option>
              <option value="Keep Individual Tips">Keep Individual Tips</option>
              <option value="Custom Formula">Custom Formula</option>
            </select>
          </div>

          {/* Manage Roles & Add Category */}
          <div className="flex flex-col gap-1 w-full sm:w-auto">
            <div className="flex items-center justify-between px-1">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Manage Roles & Add Category
              </label>
              <button
                type="button"
                onClick={() => setShowCustomInput(!showCustomInput)}
                className="text-[10px] font-bold text-blue-600 hover:underline"
              >
                {showCustomInput ? 'Select Standard Role' : '+ Custom Role'}
              </button>
            </div>

            <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-2xl border border-gray-200">
              {showCustomInput ? (
                <input
                  type="text"
                  placeholder="e.g. Senior Colorist"
                  value={customRoleInput}
                  onChange={e => setCustomRoleInput(e.target.value)}
                  className="bg-transparent border-none text-xs font-bold text-gray-800 focus:ring-0 px-2 w-full"
                />
              ) : (
                <select 
                  value={newRole} 
                  onChange={(e) => setNewRole(e.target.value)} 
                  className="bg-transparent border-none text-xs font-bold text-gray-800 focus:ring-0 cursor-pointer pr-4 truncate w-full"
                >
                  <optgroup label="Direct Service Roles">
                    {ROLES.filter(r => !isManagerialRole(r)).map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Management & Leadership">
                    {ROLES.filter(r => isManagerialRole(r)).map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </optgroup>
                </select>
              )}
              <button 
                type="button"
                onClick={handleAddRole} 
                className="bg-gray-900 text-white p-2 rounded-xl hover:bg-black transition-all shadow-md active:scale-95 flex items-center justify-center min-w-[36px]"
                title="Add Role to Allocation List"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {settings?.allocationMethod === 'Custom Formula' && (
        <div className="space-y-1.5 p-4 bg-gray-50 rounded-2xl border border-gray-200">
          <label className="text-xs font-bold text-gray-700 uppercase tracking-widest px-1 block">
            Custom Allocation Formula
          </label>
          <input 
            type="text" 
            placeholder="e.g. (tips * 0.5) + shift_bonus" 
            value={settings.customFormula || ''} 
            onChange={e => setSettings(prev => prev ? {...prev, customFormula: e.target.value} : null)} 
            className="input-field w-full text-xs font-mono" 
          />
        </div>
      )}

      {/* Mobile view: List of Cards */}
      <div className="md:hidden space-y-4">
        {roles.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <p className="text-gray-400 font-bold text-sm uppercase tracking-widest">No roles configured yet</p>
          </div>
        ) : (
          roles.map(role => (
            <div key={role.id} className={`card p-4 border border-gray-100 shadow-xs transition-all ${!role.isEligible ? 'opacity-70 bg-gray-50/70' : 'bg-white'}`}>
              <div className="flex items-start justify-between mb-4 gap-2">
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${role.isEligible ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                    <Shield size={18} />
                  </div>
                  <div className="overflow-hidden">
                    <h3 className="font-black text-gray-900 text-sm leading-tight truncate">{role.role}</h3>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={role.isEligible} 
                      onChange={(e) => handleEligibilityChange(role.id, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <button
                    type="button"
                    onClick={() => handleRemoveRole(role.id, role.role)}
                    className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg hover:bg-rose-50"
                    title={`Remove ${role.role}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              
              <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Percent size={14} className="text-gray-400" />
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Payout Split</span>
                </div>
                <div className="relative max-w-[100px]">
                  <input
                    type="number"
                    value={role.percentage}
                    onChange={(e) => handlePercentageChange(role.id, e.target.value)}
                    disabled={!role.isEligible || !isRoleBasedMethod}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 text-xs font-black text-gray-900 outline-none text-right pr-6"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-[10px]">%</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop view: Table */}
      <div className="hidden md:block overflow-hidden border border-gray-200 rounded-3xl shadow-xs bg-white">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50/80 border-b border-gray-200">
              <th className="px-6 py-4 font-bold text-gray-500 text-[10px] uppercase tracking-[0.2em]">Role Category</th>
              <th className="px-6 py-4 font-bold text-gray-500 text-[10px] uppercase tracking-[0.2em] text-center">Pool Eligibility</th>
              <th className="px-6 py-4 font-bold text-gray-500 text-[10px] uppercase tracking-[0.2em] text-right">Split Ratio (%)</th>
              <th className="px-6 py-4 font-bold text-gray-500 text-[10px] uppercase tracking-[0.2em] text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {roles.map(role => (
              <tr key={role.id} className={`hover:bg-gray-50/50 transition-colors ${!role.isEligible ? 'opacity-60 bg-gray-50/30' : ''}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl ${role.isEligible ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                      <Shield size={16} />
                    </div>
                    <div>
                      <span className="font-bold text-gray-900 text-xs block">{role.role}</span>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex justify-center items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={role.isEligible} 
                        onChange={(e) => handleEligibilityChange(role.id, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <span className={`text-[10px] font-bold uppercase ${role.isEligible ? 'text-emerald-700' : 'text-gray-400'}`}>
                      {role.isEligible ? 'Active' : 'Excluded'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="relative inline-block max-w-[120px]">
                    <input
                      type="number"
                      value={role.percentage}
                      onChange={(e) => handlePercentageChange(role.id, e.target.value)}
                      disabled={!role.isEligible || !isRoleBasedMethod}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-black text-gray-900 focus:bg-white focus:border-blue-500 outline-none text-right pr-6 disabled:opacity-30 transition-all"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-xs">%</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    type="button"
                    onClick={() => handleRemoveRole(role.id, role.role)}
                    className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all inline-flex items-center gap-1 text-xs font-bold"
                    title={`Remove ${role.role} from allocations`}
                  >
                    <Trash2 size={14} />
                    <span className="hidden sm:inline">Remove</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer / Save Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-6 border-t border-gray-100">
        <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-0.5">Ratio Balance</span>
            <div className="flex items-baseline gap-1">
              <span className={`text-3xl font-black italic tracking-tighter ${total === 100 ? 'text-blue-600' : total > 100 ? 'text-red-500' : 'text-amber-500'}`}>
                {Math.round(total * 10) / 10}
              </span>
              <span className="text-gray-400 font-bold text-sm">%</span>
            </div>
          </div>
          {isRoleBasedMethod && total !== 100 && (
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase text-amber-700 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-200">
              <AlertCircle size={14} />
              {total > 100 ? 'Total exceeds 100%' : 'Must equal 100% to save'}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {status && (
            <div className={`flex items-center gap-1 text-xs font-bold ${status.includes('Error') ? 'text-red-600' : 'text-emerald-700'}`}>
              <CheckCircle2 size={14} />
              <span>{status}</span>
            </div>
          )}
          <button 
            type="button"
            onClick={handleSave} 
            disabled={isSaveDisabled}
            className={`bg-gray-900 text-white px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-black transition-all shadow-md active:scale-[0.98] flex items-center gap-2 ${isSaveDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <span>Save Allocation Policy</span>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Smart Managerial Protection Modal */}
      {roleNotice && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <ShieldCheck size={24} />
            </div>
            <h3 className="text-base font-black text-gray-900 mb-2">
              Tip Pool Role Notice
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed mb-4">
              {roleNotice.action === 'add' ? (
                <><strong>{roleNotice.role}</strong> cannot be added to a shared employee tip pool.</>
              ) : roleNotice.action === 'percentage' ? (
                <><strong>{roleNotice.role}</strong> is a leadership role and cannot receive a percentage from shared employee tip pools.</>
              ) : (
                <><strong>{roleNotice.role}</strong> is a leadership role and cannot participate in shared employee tip pools.</>
              )}
            </p>
            <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/60 text-[11px] text-slate-700 font-medium mb-6 leading-relaxed">
              💡 <strong>Direct Tips & Solo Shifts:</strong> Managers and owners are always entitled to keep 100% of tips earned from solo shifts or services they directly provide without other staff.
            </div>
            <button
              type="button"
              onClick={() => setRoleNotice(null)}
              className="w-full py-3 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-black uppercase tracking-wider transition shadow-sm cursor-pointer"
            >
              Understood
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
